import React from "react";

import { getParentElement } from "@impero-com/common/utils/dom";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Tag } from "@impero-com/ui";
import { Select } from "antd";
import { isEqual as arrayIsEqual } from "lodash-es";
import memoizeOne from "memoize-one";

import { MultiInputHelper } from "utils/multiInputHelper";

import { BaseFormDisplay } from "./formDisplay";
import { tagRow } from "./multiSelectMultipleInput.css";

import { BaseFormField } from ".";

const { Option } = Select;

type MultiSelectMultipleInputProps<T extends string | number> =
    | ({ type: "EDIT" } & WrappedWriteProps<T>)
    | ({ type: "READ_ONLY" } & WrappedReadProps<T>);

type Wrapped<T> = T & { label: string };
type WrappedReadProps<T> = Wrapped<ReadProps<T>>;
type WrappedWriteProps<T> = Wrapped<WriteProps<T>>;

interface ReadProps<T> {
    name: string;
    values: Array<Array<T>>;
    optionLabel: (item: T) => string;
    className?: string;
    tagClassName?: string;
}

interface WriteProps<T> extends ReadProps<T> {
    onChange: (v: Array<T>) => void;
    options: Array<T>;
}

type Helper<T, D> = MultiInputHelper<Array<T>, D>;
function buildHelper<T>(): Helper<T, void> {
    return new MultiInputHelper({
        isEqual: arrayIsEqual,
        isBlank: (values: Array<T>) => values.length === 0,
        display: () => {},
    });
}

class MultiSelectMultipleInput<
    T extends string | number,
> extends React.PureComponent<MultiSelectMultipleInputProps<T>> {
    render() {
        const { props } = this;
        switch (props.type) {
            case "READ_ONLY": {
                const {
                    label,
                    name,
                    values,
                    optionLabel,
                    className,
                    tagClassName,
                } = props;
                return (
                    <WrappedReadOnlyMultiSelectMultiple<T>
                        label={label}
                        name={name}
                        values={values}
                        optionLabel={optionLabel}
                        className={className}
                        tagClassName={tagClassName}
                    />
                );
            }
            case "EDIT": {
                const {
                    label,
                    name,
                    values,
                    optionLabel,
                    options,
                    className,
                    tagClassName,
                    onChange,
                } = props;
                return (
                    <WrappedEditMultiSelectMultiple<T>
                        label={label}
                        name={name}
                        values={values}
                        optionLabel={optionLabel}
                        options={options}
                        className={className}
                        tagClassName={tagClassName}
                        onChange={onChange}
                    />
                );
            }
            default:
                return assertNever(props);
        }
    }
}

type RawReadOnlyMultiSelectMultipleInputProps<T> = ReadProps<T>;

class RawReadOnlyMultiSelectMultipleInput<
    T extends string | number,
> extends React.PureComponent<RawReadOnlyMultiSelectMultipleInputProps<T>> {
    private helper = buildHelper<T>();

    render() {
        const state = this.helper.deriveState(this.props.values);
        switch (state.kind) {
            case "EMPTY":
                return <p>{t("Empty")}</p>;
            case "IDENTICAL_VALUES":
                let value: Array<T> = [];
                if (state.kind === "IDENTICAL_VALUES") {
                    value = state.value;
                }
                const tags = value.map((v) => (
                    <Tag key={v} className={this.props.tagClassName}>
                        {this.props.optionLabel(v)}
                    </Tag>
                ));
                return <span className={tagRow}>{tags}</span>;
            case "DIFFERENT_VALUES":
                return (
                    <Tag className={this.props.tagClassName}>
                        {t("Form_Field_Multiple_Values")}
                    </Tag>
                );
            default:
                return assertNever(state);
        }
    }
}

type RawEditableMultiSelectMultipleInputProps<T> = WriteProps<T>;

class RawEditableMultiSelectMultipleInput<
    T extends string | number,
> extends React.Component<RawEditableMultiSelectMultipleInputProps<T>> {
    private helper = buildHelper<T>();

    render() {
        const state = this.helper.deriveState(this.props.values);

        const getOptions = memoizeOne((options) => {
            return options.map((option: T) => (
                <Option key={option} value={option}>
                    <p>{this.props.optionLabel(option)}</p>
                </Option>
            ));
        });
        const options = getOptions(this.props.options);

        switch (state.kind) {
            case "EMPTY":
            case "IDENTICAL_VALUES":
                let value: Array<T> = [];
                if (state.kind === "IDENTICAL_VALUES") {
                    value = state.value;
                }
                return (
                    <Select
                        mode="multiple"
                        allowClear
                        onChange={(newValue) => {
                            this.props.onChange(newValue);
                        }}
                        value={value}
                        className={this.props.className}
                        getPopupContainer={getParentElement}
                    >
                        {options}
                    </Select>
                );
            case "DIFFERENT_VALUES":
                return (
                    <Tag
                        closable
                        className={this.props.tagClassName}
                        onClose={() => {
                            this.props.onChange([]);
                        }}
                    >
                        {t("Form_Field_Multiple_Values")}
                    </Tag>
                );
            default:
                return assertNever(state);
        }
    }
}

class WrappedReadOnlyMultiSelectMultiple<
    T extends string | number,
> extends React.PureComponent<WrappedReadProps<T>> {
    render() {
        const { label, name, values, optionLabel, className, tagClassName } =
            this.props;
        return (
            <BaseFormDisplay label={label}>
                <RawReadOnlyMultiSelectMultipleInput
                    name={name}
                    values={values}
                    optionLabel={optionLabel}
                    className={className}
                    tagClassName={tagClassName}
                />
            </BaseFormDisplay>
        );
    }
}

class WrappedEditMultiSelectMultiple<
    T extends string | number,
> extends React.PureComponent<WrappedWriteProps<T>> {
    render() {
        const {
            label,
            name,
            values,
            optionLabel,
            options,
            className,
            tagClassName,
            onChange,
        } = this.props;
        return (
            <BaseFormField
                getComponent={() => (
                    <RawEditableMultiSelectMultipleInput
                        name={name}
                        values={values}
                        onChange={onChange}
                        optionLabel={optionLabel}
                        options={options}
                        className={className}
                        tagClassName={tagClassName}
                    />
                )}
                hasFeedback={false}
                label={label}
            />
        );
    }
}

export {
    MultiSelectMultipleInput,
    type MultiSelectMultipleInputProps,
    RawEditableMultiSelectMultipleInput,
    type RawEditableMultiSelectMultipleInputProps,
    RawReadOnlyMultiSelectMultipleInput,
    type RawReadOnlyMultiSelectMultipleInputProps,
};
